import posthog from 'posthog-js';
import {
  consumePostHogHandoff,
  getPostHogLocalAppOrigin,
} from './posthogHandoff';

const ALLOWED_ENVIRONMENTS = ['local', 'development', 'production'];
const APP_SURFACE = 'web_app';
const EVENT_PRODUCER = 'app_ui';
const ADVERTISING_ID_PROPERTIES = [
  'dclid',
  'epik',
  'fbclid',
  'gbraid',
  'gclid',
  'gclsrc',
  'igshid',
  'irclid',
  'li_fat_id',
  'mc_cid',
  'msclkid',
  'qclid',
  'rdt_cid',
  'sccid',
  'ttclid',
  'twclid',
  'wbraid',
  '_kx',
];
const RETAINED_ATTRIBUTION_PROPERTIES = new Set([
  'utm_campaign',
  'utm_medium',
  'utm_source',
]);
const ATTRIBUTION_PROPERTY_PATTERN = /^(?:\$initial_|\$session_entry_)?utm_(campaign|content|medium|source|term)$/;
const SAFE_ATTRIBUTION_VALUE_PATTERN = /^[a-zA-Z0-9 ._~:/+-]{1,100}$/;
const EXPLICIT_EVENT_SCHEMAS = {
  authenticated_session_started: { authentication_provider: { allowedValues: new Set(['auth0']) } },
  logout_initiated: { logout_source: { allowedValues: new Set(['logout_page', 'wrong_account']) } },
  user_logged_in: { authentication_provider: { allowedValues: new Set(['auth0']) } },
};
const INTERACTIVE_LOGIN_STORAGE_KEY = 'questri_posthog_interactive_login_pending';
const AUTOMATIC_EVENTS_RESTRICTED_ON_SENSITIVE_ROUTES = [
  '$autocapture',
  '$dead_click',
  '$heatmap',
  '$rageclick',
  '$snapshot',
];
const SENSITIVE_QUERY_PARAMETERS = [
  'connectedAccountId',
  'responderId',
  'response',
  'tenantUserRequestId',
];
const SENSITIVE_ROUTE_PATTERNS = [
  /^\/billing\/payments\/connected(?:\/|$)/,
  /^\/callback(?:\/|$)/,
  /^\/confirm-email(?:\/|$)/,
  /^\/invoices\/[^/]+(?:\/|$)/,
  /^\/public\/horses\/[^/]+\/shared\/[^/]+(?:\/|$)/,
  /^\/schedules\/[^/]+(?:\/|$)/,
];
const ROUTE_NORMALIZERS = [
  {
    pattern: /^\/public\/horses\/[^/]+\/shared\/[^/]+\/?$/,
    replacement: '/public/horses/:id/shared/:shareId',
  },
  {
    pattern: /^\/horses\/[^/]+\/notes\/[^/]+\/?$/,
    replacement: '/horses/:id/notes/:noteId',
  },
  {
    pattern: /^\/billing\/payments\/connected\/?$/,
    replacement: '/billing/payments/connected',
  },
  {
    pattern: /^\/settings\/admin\/users\/[^/]+\/?$/,
    replacement: '/settings/admin/users/:id',
  },
  {
    pattern: /^\/settings\/calendar\/(?!new\/?$)[^/]+\/?$/,
    replacement: '/settings/calendar/:id',
  },
  {
    pattern: /^\/settings\/locations\/[^/]+\/?$/,
    replacement: '/settings/locations/:id',
  },
  {
    pattern: /^\/settings\/feeds\/[^/]+\/?$/,
    replacement: '/settings/feeds/:id',
  },
  {
    pattern: /^\/settings\/tasks\/(?!new\/?$|reminders\/?$|statuses\/?$|tags\/?$)[^/]+\/?$/,
    replacement: '/settings/tasks/:id',
  },
  {
    pattern: /^\/schedules\/[^/]+\/?$/,
    replacement: '/schedules/:id',
  },
  {
    pattern: /^\/invoices\/[^/]+\/?$/,
    replacement: '/invoices/:id',
  },
  {
    pattern: /^\/horses\/[^/]+\/?$/,
    replacement: '/horses/:id',
  },
  {
    pattern: /^\/tasks\/(?!analytics\/?$|calendar\/?$|planning\/?$|reminders\/?$)[^/]+\/?$/,
    replacement: '/tasks/:id',
  },
  {
    pattern: /^\/users\/[^/]+\/?$/,
    replacement: '/users/:id',
  },
];
const URL_PROPERTY_NAMES = [
  '$current_url',
  '$initial_current_url',
  '$initial_referrer',
  '$referrer',
  '$session_entry_url',
];

let initializationAttempted = false;
let handoffPrepared = false;
let pendingHandoff = null;
let postHogClient = null;
let deploymentEnvironment = null;
let currentUserId = null;
let currentTenantId = null;
let captureEnabled = false;
let tenantOwnerUserId = null;
let lastTenantSelection = null;
let awaitingFreshTenantSelection = false;
let routeCaptureRestricted = null;
let signupFlowLandedCaptured = false;

const getCurrentRoute = () => ({
  pathname: (window.location && window.location.pathname) || '/',
  search: (window.location && window.location.search) || '',
});

const sanitizeExplicitEvent = (eventName, properties = {}) => {
  const schema = EXPLICIT_EVENT_SCHEMAS[eventName];
  if (!schema || !properties || typeof properties !== 'object') {
    return null;
  }

  const sanitizedProperties = Object.keys(schema).reduce((result, propertyName) => {
    const rule = schema[propertyName];
    const value = properties[propertyName];

    if (!rule.allowedValues || !rule.allowedValues.has(value)) {
      return result;
    }

    return {
      ...result,
      [propertyName]: value,
    };
  }, {});

  return Object.keys(sanitizedProperties).length === Object.keys(schema).length
    ? {
      eventName,
      properties: sanitizedProperties,
    }
    : null;
};

const normalizeCapturedPathname = (pathname = '/') => {
  const matchedRoute = ROUTE_NORMALIZERS.find(({ pattern }) => pattern.test(pathname));
  if (matchedRoute) {
    return matchedRoute.replacement;
  }
  return pathname;
};

const getRouteFromUrl = (value) => {
  if (typeof value !== 'string' || !value) {
    return null;
  }

  try {
    const url = new URL(value, window.location.origin);
    return {
      pathname: url.pathname,
      search: url.search,
    };
  } catch (error) {
    return null;
  }
};

const isSensitiveRoute = ({
  pathname = '/',
  search = '',
} = {}) => {
  if (SENSITIVE_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname))) {
    return true;
  }

  try {
    const queryParameters = new URLSearchParams(search);
    return SENSITIVE_QUERY_PARAMETERS.some((parameter) => queryParameters.has(parameter));
  } catch (error) {
    return true;
  }
};

const getCapturedOrigin = (value) => {
  try {
    return new URL(value, window.location.origin).origin;
  } catch (error) {
    return null;
  }
};

const sanitizeCapturedUrl = (value, applicationOrigin, originOnlyWhenExternal = false) => {
  const route = getRouteFromUrl(value);
  if (!route) {
    return '';
  }

  try {
    const url = new URL(value, window.location.origin);
    if (originOnlyWhenExternal && url.origin !== applicationOrigin) {
      return url.origin;
    }
    return `${url.origin}${normalizeCapturedPathname(route.pathname)}`;
  } catch (error) {
    return '';
  }
};

const getAttributionPropertyBaseName = (propertyName) => {
  const match = propertyName.match(ATTRIBUTION_PROPERTY_PATTERN);
  if (!match) {
    return null;
  }
  return `utm_${match[1]}`;
};

const sanitizeAttributionValue = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalizedValue = value.trim();
  if (
    !SAFE_ATTRIBUTION_VALUE_PATTERN.test(normalizedValue)
    || normalizedValue.includes('@')
  ) {
    return null;
  }
  return normalizedValue;
};

const sanitizePropertyBag = (properties = {}, applicationOrigin = window.location.origin) => {
  const sanitizedProperties = { ...properties };

  Object.keys(sanitizedProperties).forEach((propertyName) => {
    const campaignPropertyBaseName = propertyName.replace(
      /^(?:\$initial_|\$session_entry_)/,
      '',
    );
    if (ADVERTISING_ID_PROPERTIES.includes(campaignPropertyBaseName)) {
      delete sanitizedProperties[propertyName];
      return;
    }

    const attributionProperty = getAttributionPropertyBaseName(propertyName);
    if (!attributionProperty) {
      return;
    }
    if (!RETAINED_ATTRIBUTION_PROPERTIES.has(attributionProperty)) {
      delete sanitizedProperties[propertyName];
      return;
    }

    const sanitizedValue = sanitizeAttributionValue(sanitizedProperties[propertyName]);
    if (sanitizedValue) {
      sanitizedProperties[propertyName] = sanitizedValue;
      return;
    }
    delete sanitizedProperties[propertyName];
  });

  URL_PROPERTY_NAMES.forEach((propertyName) => {
    if (Object.prototype.hasOwnProperty.call(sanitizedProperties, propertyName)) {
      const isReferrer = propertyName === '$referrer' || propertyName === '$initial_referrer';
      sanitizedProperties[propertyName] = sanitizeCapturedUrl(
        sanitizedProperties[propertyName],
        applicationOrigin,
        isReferrer,
      );
    }
  });

  if (typeof sanitizedProperties.$pathname === 'string') {
    sanitizedProperties.$pathname = normalizeCapturedPathname(sanitizedProperties.$pathname);
  }

  return sanitizedProperties;
};

const getCapturedEventRoute = (event) => {
  const currentUrl = event && event.properties && event.properties.$current_url;
  return getRouteFromUrl(currentUrl) || getCurrentRoute();
};

const getRuntimeConfiguration = (runtimeEnvironment = {}) => {
  if (runtimeEnvironment.POSTHOG_ENABLED !== 'true') {
    return null;
  }

  const projectKey = typeof runtimeEnvironment.POSTHOG_KEY === 'string'
    ? runtimeEnvironment.POSTHOG_KEY.trim()
    : '';
  const host = typeof runtimeEnvironment.POSTHOG_HOST === 'string'
    ? runtimeEnvironment.POSTHOG_HOST.trim()
    : '';
  const environment = typeof runtimeEnvironment.POSTHOG_ENVIRONMENT === 'string'
    ? runtimeEnvironment.POSTHOG_ENVIRONMENT.trim().toLowerCase()
    : '';
  const localAppOrigin = getPostHogLocalAppOrigin(
    runtimeEnvironment.POSTHOG_LOCAL_APP_ORIGIN,
  );

  if (
    !projectKey
    || !host
    || !ALLOWED_ENVIRONMENTS.includes(environment)
    || !localAppOrigin
  ) {
    return null;
  }

  try {
    const hostUrl = new URL(host);
    if (hostUrl.protocol !== 'https:' && hostUrl.protocol !== 'http:') {
      return null;
    }
  } catch (error) {
    return null;
  }

  return {
    environment,
    host,
    localAppOrigin,
    projectKey,
  };
};

const disableClient = (client) => {
  captureEnabled = false;
  postHogClient = null;
  currentUserId = null;
  currentTenantId = null;
  tenantOwnerUserId = null;
  lastTenantSelection = null;
  awaitingFreshTenantSelection = false;
  pendingHandoff = null;

  try {
    client.stopSessionRecording();
  } catch (error) {
    // PostHog must remain isolated from application behavior, including its own shutdown path.
  }

  try {
    client.set_config({
      autocapture: false,
      capture_dead_clicks: false,
      capture_heatmaps: false,
      capture_pageleave: false,
      capture_pageview: false,
      disable_session_recording: true,
    });
  } catch (error) {
    // The before_send gate remains the final outbound guard if SDK teardown fails.
  }
};

const registerAnalyticsContext = (client) => {
  client.register({
    app_surface: APP_SURFACE,
    deployment_environment: deploymentEnvironment,
    event_producer: EVENT_PRODUCER,
  });
};

const resetClient = () => {
  if (!postHogClient) {
    return false;
  }

  const client = postHogClient;

  try {
    client.reset();
    registerAnalyticsContext(client);
    currentUserId = null;
    currentTenantId = null;
    return true;
  } catch (error) {
    currentUserId = null;
    currentTenantId = null;
    disableClient(client);
    return false;
  }
};

const runWithClient = (operation) => {
  if (!postHogClient) {
    return false;
  }

  try {
    operation(postHogClient);
    return true;
  } catch (error) {
    return false;
  }
};

const runContextOperation = (operation) => {
  if (!postHogClient) {
    return false;
  }

  const client = postHogClient;
  try {
    operation(client);
    return true;
  } catch (error) {
    disableClient(client);
    return false;
  }
};

const normalizeIdentifier = (identifier) => {
  if (typeof identifier === 'string') {
    return identifier.trim();
  }
  if (typeof identifier === 'number' && Number.isFinite(identifier)) {
    return String(identifier);
  }
  return '';
};

const filterCapturedEvent = (event) => {
  if (!captureEnabled || !event) {
    return null;
  }

  const automaticEventRestricted = AUTOMATIC_EVENTS_RESTRICTED_ON_SENSITIVE_ROUTES.includes(event.event)
    && isSensitiveRoute(getCapturedEventRoute(event));
  if (automaticEventRestricted) {
    return null;
  }

  const applicationOrigin = getCapturedOrigin(event.properties && event.properties.$current_url) || window.location.origin;
  const properties = sanitizePropertyBag(event.properties, applicationOrigin);
  if (properties.$set) {
    properties.$set = sanitizePropertyBag(properties.$set, applicationOrigin);
  }
  if (properties.$set_once) {
    properties.$set_once = sanitizePropertyBag(properties.$set_once, applicationOrigin);
  }

  return {
    ...event,
    properties,
  };
};

export const preparePostHogHandoff = (
  runtimeEnvironment,
  browserEnvironment = {},
) => {
  if (handoffPrepared) {
    return !!pendingHandoff;
  }
  handoffPrepared = true;

  const configuration = getRuntimeConfiguration(runtimeEnvironment);
  pendingHandoff = consumePostHogHandoff({
    environment: configuration && configuration.environment,
    localAppOrigin: configuration && configuration.localAppOrigin,
    ...browserEnvironment,
  });
  return !!pendingHandoff;
};

export const initializePostHog = (runtimeEnvironment) => {
  if (initializationAttempted) {
    return !!postHogClient;
  }
  initializationAttempted = true;

  const configuration = getRuntimeConfiguration(runtimeEnvironment);
  if (!configuration) {
    return false;
  }

  deploymentEnvironment = configuration.environment;
  if (routeCaptureRestricted === null) {
    routeCaptureRestricted = isSensitiveRoute(getCurrentRoute());
  }
  const acceptedHandoff = pendingHandoff;
  pendingHandoff = null;

  try {
    const initializationOptions = {
      api_host: configuration.host,
      defaults: '2026-01-30',
      autocapture: true,
      capture_pageview: 'history_change',
      capture_pageleave: 'if_capture_pageview',
      rageclick: {
        content_ignorelist: true,
        ignore_text_selection: true,
      },
      capture_dead_clicks: true,
      capture_heatmaps: true,
      disable_session_recording: routeCaptureRestricted,
      session_recording: {
        maskAllInputs: true,
        recordBody: false,
        recordHeaders: false,
      },
      enable_recording_console_log: true,
      capture_performance: true,
      capture_exceptions: true,
      mask_all_text: false,
      disable_capture_url_hashes: true,
      persistence: 'localStorage+cookie',
      cross_subdomain_cookie: true,
      person_profiles: 'identified_only',
      request_batching: true,
      opt_out_capturing_by_default: false,
      debug: false,
      advanced_disable_feature_flags: false,
      disable_surveys: false,
      disable_product_tours: false,
      disable_conversations: false,
      disable_web_experiments: false,
      before_send: filterCapturedEvent,
      on_request_error: () => undefined,
      loaded: (client) => {
        try {
          const persistedUserId = normalizeIdentifier(client.get_property('$user_id'));
          const handoffBootstrapped = acceptedHandoff
            && typeof client.get_distinct_id === 'function'
            && typeof client.get_session_id === 'function'
            && client.get_distinct_id() === acceptedHandoff.distinctID
            && client.get_session_id() === acceptedHandoff.sessionID;
          let initialUserId = persistedUserId;
          if (handoffBootstrapped) {
            initialUserId = null;
          }
          client.unregister('tenant_id');
          registerAnalyticsContext(client);
          currentUserId = initialUserId;
          currentTenantId = null;
          tenantOwnerUserId = initialUserId;
          lastTenantSelection = null;
          awaitingFreshTenantSelection = false;
          postHogClient = client;
          captureEnabled = true;

          if (handoffBootstrapped && !signupFlowLandedCaptured) {
            try {
              client.capture('signup_flow_landed', {
                destination_surface: 'web_app',
                handoff_version: Number(acceptedHandoff.version),
                source_surface: 'marketing_web',
              });
              signupFlowLandedCaptured = true;
            } catch (error) {
              // The handoff event must not affect application or analytics availability.
            }
          }
        } catch (error) {
          disableClient(client);
        }
      },
    };
    if (acceptedHandoff) {
      initializationOptions.bootstrap = {
        distinctID: acceptedHandoff.distinctID,
        sessionID: acceptedHandoff.sessionID,
      };
    }
    posthog.init(configuration.projectKey, initializationOptions);
  } catch (error) {
    disableClient(posthog);
  }

  return !!postHogClient;
};

export const capturePostHogEvent = (eventName, properties, options) => {
  const explicitEvent = sanitizeExplicitEvent(eventName, properties);
  if (!explicitEvent) {
    return false;
  }
  return runWithClient((client) => {
    client.capture(
      explicitEvent.eventName,
      explicitEvent.properties,
      options,
    );
  });
};

export const synchronizePostHogRoute = ({
  pathname = '/',
  search = '',
} = {}) => {
  const shouldRestrictCapture = isSensitiveRoute({
    pathname,
    search,
  });
  const restrictionChanged = routeCaptureRestricted !== shouldRestrictCapture;
  routeCaptureRestricted = shouldRestrictCapture;

  if (!postHogClient) {
    return false;
  }
  if (!restrictionChanged) {
    return true;
  }

  return runWithClient((client) => {
    if (shouldRestrictCapture) {
      client.stopSessionRecording();
      return;
    }
    client.startSessionRecording();
  });
};

export const markPostHogInteractiveLogin = () => {
  try {
    window.sessionStorage.setItem(INTERACTIVE_LOGIN_STORAGE_KEY, 'true');
    return true;
  } catch (error) {
    return false;
  }
};

export const consumePostHogInteractiveLogin = () => {
  try {
    const isInteractiveLogin = window.sessionStorage.getItem(INTERACTIVE_LOGIN_STORAGE_KEY) === 'true';
    window.sessionStorage.removeItem(INTERACTIVE_LOGIN_STORAGE_KEY);
    return isInteractiveLogin;
  } catch (error) {
    return false;
  }
};

export const identifyPostHogUser = ({
  id,
  email,
  name,
} = {}) => {
  const userId = normalizeIdentifier(id);
  if (!userId || !postHogClient) {
    return false;
  }

  if (currentUserId && currentUserId !== userId && !resetClient()) {
    return false;
  }

  const personProperties = {};
  if (email) {
    personProperties.email = email;
  }
  if (name) {
    personProperties.name = name;
  }

  const identified = runContextOperation((client) => {
    client.identify(userId, personProperties);
  });
  if (identified) {
    currentUserId = userId;
  }
  return identified;
};

export const clearPostHogTenant = () => {
  const cleared = runContextOperation((client) => {
    client.unregister('tenant_id');
  });
  if (cleared) {
    currentTenantId = null;
  }
  return cleared;
};

export const registerPostHogTenant = (tenantId) => {
  const normalizedTenantId = normalizeIdentifier(tenantId);
  if (!normalizedTenantId) {
    return clearPostHogTenant();
  }
  if (normalizedTenantId === currentTenantId) {
    return true;
  }

  const registered = runContextOperation((client) => {
    client.register({ tenant_id: normalizedTenantId });
  });
  if (registered) {
    currentTenantId = normalizedTenantId;
  }
  return registered;
};

export const synchronizePostHogTenant = ({
  isAuthenticated = false,
  selection = null,
  tenantId,
  userId,
} = {}) => {
  if (!postHogClient) {
    return false;
  }

  const normalizedUserId = normalizeIdentifier(userId);
  if (!isAuthenticated || !normalizedUserId) {
    return clearPostHogTenant();
  }

  const authenticatedUserChanged = !!tenantOwnerUserId && tenantOwnerUserId !== normalizedUserId;
  if (authenticatedUserChanged) {
    awaitingFreshTenantSelection = true;
    lastTenantSelection = selection;
    tenantOwnerUserId = normalizedUserId;
    return clearPostHogTenant();
  }

  // GET_BARNS_SUCCESS replaces the selected barn object; until then, keep the prior account's selection detached.
  if (awaitingFreshTenantSelection && lastTenantSelection === selection) {
    return clearPostHogTenant();
  }

  awaitingFreshTenantSelection = false;
  lastTenantSelection = selection;
  tenantOwnerUserId = normalizedUserId;
  return registerPostHogTenant(tenantId);
};

export const resetPostHog = () => resetClient();

export const resetPostHogIfIdentified = () => {
  if (!postHogClient) {
    return false;
  }
  if (!currentUserId) {
    return true;
  }
  return resetClient();
};
